/**
 * 15 个 ZLR 法则的全屏环境视觉（设计方案 §16.3）。
 * 画在屏幕空间，不随相机平移。
 */

import { Graphics } from 'pixi.js';
import { LAWS } from '../../sim/data/laws.ts';

export class LawEffects {
  readonly overlay = new Graphics();
  private t = 0;
  private shakeX = 0;
  private shakeY = 0;

  get shake(): { x: number; y: number } {
    return { x: this.shakeX, y: this.shakeY };
  }

  update(dt: number, lawId: number, w: number, h: number): void {
    this.t += dt;
    this.overlay.clear();
    this.shakeX = 0;
    this.shakeY = 0;
    if (lawId <= 0) return;

    const law = LAWS[lawId]!;
    const pulse = 0.5 + 0.5 * Math.sin(this.t * 1.6);

    // 底色滤镜
    const baseAlpha = lawId === 8 || lawId === 15 ? 0.14 : 0.08;
    this.overlay.rect(0, 0, w, h);
    this.overlay.fill({ color: law.color, alpha: baseAlpha + pulse * 0.03 });

    switch (lawId) {
      case 1: // 静滞：缓慢扫描的粘稠波
        this.scan(w, h, 0x9aa8b8, 0.07, 28);
        break;
      case 2: // 辐射：角落绿雾
        this.vignette(w, h, 0x3a8028, 0.22);
        this.bolts(w, h, 0x88e060, 3);
        break;
      case 3: // 引力：径向暗角
        this.vignette(w, h, 0x2a1840, 0.2);
        break;
      case 4: // 信息湍流：噪点扫描线
        this.scanlines(w, h, 0xc46f6f, 0.12);
        break;
      case 5: // 量子静默：冷蓝静场
        this.vignette(w, h, 0x184060, 0.18);
        break;
      case 6: // 时间褶皱：金色尘埃带
        this.scan(w, h, 0xd4c070, 0.08, 48);
        break;
      case 7: // 虚空回响
        this.vignette(w, h, 0x301848, 0.2);
        break;
      case 8: // 法则崩塌：震动 + 裂痕
        this.shakeX = (Math.random() - 0.5) * 5;
        this.shakeY = (Math.random() - 0.5) * 4;
        this.cracks(w, h);
        this.vignette(w, h, 0x400808, 0.28);
        break;
      case 9: // 电磁风暴
        this.scanlines(w, h, 0x70e0e8, 0.1);
        if (Math.random() > 0.7) this.bolts(w, h, 0xa8ffff, 2);
        break;
      case 10: // 因果逆流：残影由渲染器另绘，这里做暖色偏移
        this.scan(w, h, 0xc89060, 0.06, 64);
        break;
      case 11: // 熵增
        this.vignette(w, h, 0x401808, 0.2);
        break;
      case 12: // 维度薄膜
        this.scan(w, h, 0x7080d0, 0.07, 22);
        break;
      case 13: // 生命涌流
        this.vignette(w, h, 0x184018, 0.14);
        break;
      case 14: // 记忆风暴
        this.vignette(w, h, 0x401838, 0.16);
        break;
      case 15: { // 玄镜凝视：巨大几何眼
        this.vignette(w, h, 0x302008, 0.22);
        const cx = w * 0.5;
        const cy = h * 0.16;
        const rot = this.t * 0.15;
        this.overlay.ellipse(cx, cy, 90, 36);
        this.overlay.stroke({ color: 0xe8c84a, width: 2, alpha: 0.55 });
        this.overlay.circle(cx + Math.cos(rot) * 18, cy, 14);
        this.overlay.fill({ color: 0x1a1008, alpha: 0.7 });
        this.overlay.circle(cx + Math.cos(rot) * 18, cy, 6);
        this.overlay.fill({ color: 0xe8c84a, alpha: 0.85 });
        break;
      }
      default:
        break;
    }
  }

  private vignette(w: number, h: number, color: number, a: number): void {
    this.overlay.rect(0, 0, w, h);
    this.overlay.fill({ color, alpha: a * 0.35 });
    const m = Math.max(w, h) * 0.08;
    this.overlay.rect(0, 0, w, m);
    this.overlay.fill({ color, alpha: a });
    this.overlay.rect(0, h - m, w, m);
    this.overlay.fill({ color, alpha: a });
    this.overlay.rect(0, 0, m, h);
    this.overlay.fill({ color, alpha: a * 0.8 });
    this.overlay.rect(w - m, 0, m, h);
    this.overlay.fill({ color, alpha: a * 0.8 });
  }

  private scanlines(w: number, h: number, color: number, a: number): void {
    const step = 4;
    const off = (this.t * 40) % step;
    for (let y = -step + off; y < h; y += step) {
      this.overlay.rect(0, y, w, 1);
      this.overlay.fill({ color, alpha: a });
    }
  }

  private scan(w: number, h: number, color: number, a: number, period: number): void {
    const y = ((this.t * 40) % (h + period)) - period;
    this.overlay.rect(0, y, w, 10);
    this.overlay.fill({ color, alpha: a });
  }

  private bolts(w: number, h: number, color: number, n: number): void {
    for (let i = 0; i < n; i++) {
      if (Math.random() > 0.35) continue;
      let x = Math.random() * w;
      let y = 0;
      this.overlay.moveTo(x, y);
      while (y < h * 0.55) {
        x += (Math.random() - 0.5) * 80;
        y += 20 + Math.random() * 40;
        this.overlay.lineTo(x, y);
      }
      this.overlay.stroke({ color, width: 1.5, alpha: 0.35 });
    }
  }

  private cracks(w: number, h: number): void {
    for (let i = 0; i < 5; i++) {
      const x0 = Math.random() * w;
      const y0 = Math.random() * h;
      this.overlay.moveTo(x0, y0);
      let x = x0;
      let y = y0;
      for (let k = 0; k < 4; k++) {
        x += (Math.random() - 0.5) * 90;
        y += 10 + Math.random() * 50;
        this.overlay.lineTo(x, y);
      }
      this.overlay.stroke({ color: 0xff8060, width: 1.2, alpha: 0.35 });
    }
  }
}
